import cx from "classnames";
import { useCallback, useEffect, useRef, useState } from "react";
import { match } from "ts-pattern";
import { t } from "ttag";

import EmptyCodeResult from "assets/img/empty-states/code.svg";
import { CodeEditor } from "metabase/common/components/CodeEditor";
import {
  ActionIcon,
  Box,
  Flex,
  Group,
  HoverCard,
  Icon,
  Loader,
  Stack,
  Text,
  Tooltip,
  UnstyledButton,
} from "metabase/ui";
import type Question from "metabase-lib/v1/Question";
import type { VisualizationSettings } from "metabase-types/api";

import { useWorkbenchActions } from "../context";
import type {
  AdhocResult,
  AdhocResultId,
  AdhocTabId,
  AdhocTabRuntime,
} from "../types";

import S from "./ResultArea.module.css";
import { ResultPanel } from "./ResultPanel";
import type { ResultSidebarPanel } from "./ResultSidebar";

export type ResultAreaProps = {
  tabId: AdhocTabId;
  runtime: AdhocTabRuntime;
  isActive: boolean;
  activeResult: AdhocResult | undefined;
  resultQuestion: Question;
  chartPanel: ResultSidebarPanel | null;
  isShowingRawTable: boolean;
  onToggleChartPanel: (panel: ResultSidebarPanel) => void;
  onToggleRawTable: (isShowingRawTable: boolean) => void;
  onSelectVisualization: (question: Question) => void;
  onChangeVisualizationSettings: (settings: VisualizationSettings) => void;
};

/**
 * The result half of a query tab: one sub-tab per run, newest last.
 *
 * Only the active result is mounted. Keeping every chart in every result alive
 * across every tab is unbounded memory, and losing a table's scroll position
 * when switching results is a far cheaper trade than losing editor state.
 */
export function ResultArea({
  tabId,
  runtime,
  isActive,
  activeResult,
  resultQuestion,
  chartPanel,
  isShowingRawTable,
  onToggleChartPanel,
  onToggleRawTable,
  onSelectVisualization,
  onChangeVisualizationSettings,
}: ResultAreaProps) {
  const actions = useWorkbenchActions();

  if (runtime.results.length === 0) {
    return <ResultAreaEmptyState />;
  }

  return (
    <Flex className={S.area} direction="column">
      <ResultTabBar
        results={runtime.results}
        activeResultId={runtime.activeResultId}
        onSelect={(resultId) => actions.selectResult(tabId, resultId)}
        onClose={(resultId) => actions.closeResult(tabId, resultId)}
      />
      {activeResult && (
        <ResultPanel
          key={activeResult.id}
          result={activeResult}
          question={resultQuestion}
          isActive={isActive}
          isShowingRawTable={isShowingRawTable}
          chartPanel={chartPanel}
          onToggleRawTable={onToggleRawTable}
          onToggleChartPanel={onToggleChartPanel}
          onSelectVisualization={onSelectVisualization}
          onChangeVisualizationSettings={onChangeVisualizationSettings}
        />
      )}
    </Flex>
  );
}

function ResultAreaEmptyState() {
  return (
    <Flex
      className={S.area}
      align="center"
      justify="center"
      data-testid="adhoc-result-empty-state"
    >
      <Stack maw="25rem" gap={0} ta="center" align="center">
        <Box maw="3rem" mb="0.75rem">
          <img src={EmptyCodeResult} alt="" />
        </Box>
        <Text c="text-medium">{t`Run a query to see results here.`}</Text>
      </Stack>
    </Flex>
  );
}

type ResultTabBarProps = {
  results: AdhocResult[];
  activeResultId: AdhocResultId | null;
  onSelect: (resultId: AdhocResultId) => void;
  onClose: (resultId: AdhocResultId) => void;
};

const COPY_FEEDBACK_MS = 2000;
const SQL_PREVIEW_WIDTH = "min(42rem, calc(100vw - 2rem))";

/**
 * Status marker for a result chip.
 *
 * Decorative on purpose: an icon with its own label would be folded into the
 * tab's accessible name ("warning icon Result 1"). The failure itself is
 * announced by the error message rendered in the result panel.
 */
function ResultStatusIcon({ result }: { result: AdhocResult }) {
  const icon = match(result.status)
    .with("running", () => <Loader size="xs" />)
    .with("error", () => <Icon name="warning" size={12} c="danger" />)
    .with("cancelled", () => <Icon name="close" size={12} c="text-light" />)
    .otherwise(() => null);

  if (icon == null) {
    return null;
  }

  const label = match(result.status)
    .with("running", () => t`This query is running`)
    .with("error", () => t`This query failed`)
    .otherwise(() => t`This query was cancelled`);

  return (
    <Tooltip label={label}>
      <Box component="span" aria-hidden lh={0}>
        {icon}
      </Box>
    </Tooltip>
  );
}

function ResultSqlPreview({
  result,
  isActive,
  onSelect,
}: {
  result: AdhocResult;
  isActive: boolean;
  onSelect: () => void;
}) {
  const [isCopied, setIsCopied] = useState(false);
  const timeoutRef = useRef<number | null>(null);

  useEffect(() => {
    return () => {
      if (timeoutRef.current != null) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  const handleCopy = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(result.sql);
      setIsCopied(true);
      if (timeoutRef.current != null) {
        clearTimeout(timeoutRef.current);
      }
      timeoutRef.current = window.setTimeout(
        () => setIsCopied(false),
        COPY_FEEDBACK_MS,
      );
    } catch (error) {
      console.error("Could not copy the result SQL to the clipboard", error);
    }
  }, [result.sql]);

  const copyLabel = isCopied ? t`Copied!` : t`Copy SQL`;

  return (
    <HoverCard
      position="top-start"
      offset={6}
      openDelay={250}
      closeDelay={150}
      shadow="md"
      withinPortal
    >
      <HoverCard.Target>
        <UnstyledButton
          role="tab"
          aria-selected={isActive}
          className={S.label}
          data-status={result.status}
          onClick={onSelect}
        >
          <Group gap="xs" wrap="nowrap">
            <ResultStatusIcon result={result} />
            <Text size="sm" fw={isActive ? "bold" : undefined} lh={1}>
              {result.name}
            </Text>
          </Group>
        </UnstyledButton>
      </HoverCard.Target>
      <HoverCard.Dropdown
        className={S.sqlPreviewDropdown}
        data-testid="adhoc-result-sql-preview-card"
        w={SQL_PREVIEW_WIDTH}
      >
        <Box className={S.sqlPreview}>
          <Group
            className={S.sqlPreviewHeader}
            justify="space-between"
            gap="sm"
            wrap="nowrap"
          >
            <Group gap="xs" wrap="nowrap">
              <Icon name="sql" size={14} c="white" aria-hidden />
              <Text size="xs" fw="bold" c="white">
                {t`Executed SQL`}
              </Text>
              <Text size="xs" c="white">
                {result.name}
              </Text>
            </Group>
            <Tooltip label={copyLabel}>
              <ActionIcon
                size="sm"
                variant="subtle"
                c="white"
                aria-label={copyLabel}
                onClick={handleCopy}
              >
                <Icon name={isCopied ? "check" : "copy"} size={14} />
              </ActionIcon>
            </Tooltip>
          </Group>
          <CodeEditor
            className={S.sqlEditor}
            data-testid="adhoc-result-sql-preview"
            language="sql"
            lineNumbers={false}
            readOnly
            value={result.sql}
          />
        </Box>
      </HoverCard.Dropdown>
    </HoverCard>
  );
}

/** The "Result 1 / Result 2 / …" strip under a tab's editor. */
function ResultTabBar({
  results,
  activeResultId,
  onSelect,
  onClose,
}: ResultTabBarProps) {
  return (
    <Flex
      className={S.bar}
      role="tablist"
      aria-label={t`Query results`}
      data-testid="adhoc-result-tab-bar"
    >
      {results.map((result) => {
        const isActive = result.id === activeResultId;
        return (
          <Flex
            key={result.id}
            className={cx(S.tab, { [S.active]: isActive })}
            align="center"
          >
            <ResultSqlPreview
              result={result}
              isActive={isActive}
              onSelect={() => onSelect(result.id)}
            />
            <ActionIcon
              size="xs"
              variant="subtle"
              aria-label={t`Close ${result.name}`}
              onClick={() => onClose(result.id)}
            >
              <Icon name="close" size={10} />
            </ActionIcon>
          </Flex>
        );
      })}
    </Flex>
  );
}
